import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer } from 'recharts';
import { useMaterialCountryProduction } from '../../hooks';

const SpiderChart = ({
  materialCode,
  comparisons = [],
  materialName,
  mainColor = '#8B5CF6'
}) => {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState('material1');

  // Fetch country production data from API for primary material
  const { data: productionData, loading, error } = useMaterialCountryProduction(materialCode);

  // Fetch country production data for comparison materials (if materialCode is provided)
  const { data: comparisonData1 } = useMaterialCountryProduction(
    comparisons[0]?.code || comparisons[0]?.materialCode,
    { enabled: comparisons.length >= 1 && !!(comparisons[0]?.code || comparisons[0]?.materialCode) }
  );

  const { data: comparisonData2 } = useMaterialCountryProduction(
    comparisons[1]?.code || comparisons[1]?.materialCode,
    { enabled: comparisons.length >= 2 && !!(comparisons[1]?.code || comparisons[1]?.materialCode) }
  );

  // Calculate total production from countries for primary material
  const totalProduction = useMemo(() => {
    if (!productionData?.countries) return 3456763;
    return productionData.countries.reduce((sum, country) => sum + country.value, 0);
  }, [productionData]);

  // Calculate total production for comparison material 1
  const totalProductionComp1 = useMemo(() => {
    if (!comparisonData1?.countries) return 3456763;
    return comparisonData1.countries.reduce((sum, country) => sum + country.value, 0);
  }, [comparisonData1]);

  // Calculate total production for comparison material 2
  const totalProductionComp2 = useMemo(() => {
    if (!comparisonData2?.countries) return 3456763;
    return comparisonData2.countries.reduce((sum, country) => sum + country.value, 0);
  }, [comparisonData2]);

  // Use API data or fallback to dummy data
  const materialsData = useMemo(() => ({
    material1: {
      name: materialName,
      color: mainColor,
      totalProduction: totalProduction,
      countries: productionData?.countries || [
        { country: 'CHN', name: 'CHINA', value: 1254456, change: -18 },
        { country: 'CAN', name: 'CANADA', value: 856789, change: -15 },
        { country: 'BRA', name: 'BRAZIL', value: 654321, change: -12 },
        { country: 'ARG', name: 'ARGENTINA', value: 456789, change: -20 },
        { country: 'RUS', name: 'RUSSIA', value: 987654, change: -8 },
        { country: 'AUS', name: 'AUSTRALIA', value: 743210, change: -14 }
      ]
    },
    material2: {
      name: comparisons[0]?.name || t('charts.spider.material2'),
      color: comparisons[0]?.color || '#FF8C00',
      totalProduction: totalProductionComp1,
      countries: comparisonData1?.countries || [
        { country: 'CHN', name: 'CHINA', value: 1100000, change: -15 },
        { country: 'CAN', name: 'CANADA', value: 1200000, change: -10 },
        { country: 'BRA', name: 'BRAZIL', value: 800000, change: -22 },
        { country: 'ARG', name: 'ARGENTINA', value: 600000, change: -25 },
        { country: 'RUS', name: 'RUSSIA', value: 1050000, change: -19 },
        { country: 'AUS', name: 'AUSTRALIA', value: 950000, change: -16 }
      ]
    },
    material3: {
      name: comparisons[1]?.name || t('charts.spider.material3'),
      color: comparisons[1]?.color || '#32CD32',
      totalProduction: totalProductionComp2,
      countries: comparisonData2?.countries || [
        { country: 'CHN', name: 'CHINA', value: 900000, change: -12 },
        { country: 'CAN', name: 'CANADA', value: 1400000, change: -8 },
        { country: 'BRA', name: 'BRAZIL', value: 1100000, change: -19 },
        { country: 'ARG', name: 'ARGENTINA', value: 750000, change: -28 },
        { country: 'RUS', name: 'RUSSIA', value: 650000, change: -17 },
        { country: 'AUS', name: 'AUSTRALIA', value: 1250000, change: -6 }
      ]
    }
  }), [productionData, comparisonData1, comparisonData2, materialName, mainColor, totalProduction, totalProductionComp1, totalProductionComp2, comparisons, t]);

  // Calculate view mode based on comparison count
  const getViewMode = () => {
    if (comparisons.length === 0) return 'single';
    if (comparisons.length === 1) return 'dual';
    return 'triple'; // 2 or more comparisons
  };

  const viewMode = getViewMode();

  // Calculate dynamic domain for radar chart
  const getRadarDomain = useMemo(() => {
    const allValues = [];

    // Collect all values from all materials
    Object.values(materialsData).forEach(material => {
      material.countries.forEach(country => {
        allValues.push(country.value);
      });
    });

    if (allValues.length === 0) return [0, 1600000];

    const maxValue = Math.max(...allValues);
    // Add 10% padding to the maximum value
    const domainMax = Math.ceil(maxValue * 1.1);

    return [0, domainMax];
  }, [materialsData]);

  // Transform data for radar chart - ensure at least 6 axes for visual balance
  const getRadarData = useMemo(() => {
    // Find per-material max to normalize shapes (keeps shape comparable even if magnitudes differ)
    const materialMax = {
      material1: Math.max(
        1,
        ...materialsData.material1.countries.map((c) => Math.max(0, c.value || 0)),
        1
      ),
      material2: Math.max(
        1,
        ...materialsData.material2.countries.map((c) => Math.max(0, c.value || 0)),
        1
      ),
      material3: Math.max(
        1,
        ...materialsData.material3.countries.map((c) => Math.max(0, c.value || 0)),
        1
      ),
    };

    const placeholderValue = 5; // use small non-zero bulge in normalized scale (5%)

    // Get unique country codes from all materials
    const countrySet = new Set();

    // Collect countries from material1 (always present)
    materialsData.material1.countries.forEach(c => countrySet.add(c.country));

    // Collect countries from material2 if in dual/triple view
    if (viewMode === 'dual' || viewMode === 'triple') {
      materialsData.material2.countries.forEach(c => countrySet.add(c.country));
    }

    // Collect countries from material3 if in triple view
    if (viewMode === 'triple') {
      materialsData.material3.countries.forEach(c => countrySet.add(c.country));
    }

    const realCountries = Array.from(countrySet).sort();
    let countries = [...realCountries];

    // Build a 6-axis layout; if only two real countries, place them on opposite sides
    if (realCountries.length === 1) {
      countries = [
        realCountries[0],
        '—2',
        '—3',
        '—4',
        '—5',
        '—6',
      ];
    } else if (realCountries.length === 2) {
      // Force opposing positions (0 and 3) so the radar keeps shape
      countries = [
        realCountries[0],
        '—2',
        '—3',
        realCountries[1],
        '—5',
        '—6',
      ];
    } else {
      countries = [...realCountries];
      if (countries.length < 6) {
        const needed = 6 - countries.length;
        for (let i = 0; i < needed; i++) {
          countries.push(`—${i + 1}`);
        }
      }
    }

    return countries.map(country => {
      const isPlaceholder = country.startsWith('—');
      const dataPoint = { country };

      // Always include material1 data
      const m1Val = Math.max(0, materialsData.material1.countries.find(c => c.country === country)?.value ?? 0);
      const v1 = isPlaceholder ? placeholderValue : m1Val;
      dataPoint.Material1 = (v1 / materialMax.material1) * 100;

      // Include material2 data if dual or triple view
      if (viewMode === 'dual' || viewMode === 'triple') {
        const m2Val = Math.max(0, materialsData.material2.countries.find(c => c.country === country)?.value ?? 0);
        const v2 = isPlaceholder ? placeholderValue : m2Val;
        dataPoint.Material2 = (v2 / materialMax.material2) * 100;
      }

      // Include material3 data if triple view
      if (viewMode === 'triple') {
        const m3Val = Math.max(0, materialsData.material3.countries.find(c => c.country === country)?.value ?? 0);
        const v3 = isPlaceholder ? placeholderValue : m3Val;
        dataPoint.Material3 = (v3 / materialMax.material3) * 100;
      }

      return dataPoint;
    });
  }, [materialsData, viewMode]);

  const formatNumber = (num) => {
    const locale = (i18n.resolvedLanguage || i18n.language || 'en').toLowerCase().startsWith('de')
      ? 'de-DE'
      : (i18n.resolvedLanguage || i18n.language || 'en').toLowerCase().startsWith('zh')
        ? 'zh-CN'
        : 'en-US';
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  const getCurrentMaterials = () => {
    if (viewMode === 'single') return [materialsData.material1];
    if (viewMode === 'dual') return [materialsData.material1, materialsData.material2];
    return [materialsData.material1, materialsData.material2, materialsData.material3];
  };

  const getDisplayedMaterial = () => {
    if (viewMode === 'single') return materialsData.material1;
    return materialsData[activeTab];
  };

  const shortenName = (name, max = 14) => {
    if (!name) return '';
    const trimmed = name.trim();

    // Keep prefix + a short region hint if parentheses are present
    let candidate = trimmed;
    if (trimmed.includes('(') && trimmed.includes(')')) {
      const prefix = trimmed.split('(')[0].trim();
      const inside = trimmed.split('(')[1].split(')')[0].trim();
      const region = inside.split(/\s+/)[0] || '';
      candidate = region ? `${prefix} (${region})` : prefix;
    }

    if (candidate.length <= max) return candidate;

    // Build initials if it's still too long
    const words = candidate.replace(/[()]/g, ' ').split(/\s+/).filter(Boolean);
    if (words.length > 1) {
      const initials = words.slice(0, 3).map((w) => w[0]).join('').toUpperCase();
      if (initials.length <= max) return initials;
    }

    return `${candidate.slice(0, max - 1)}…`;
  };

  return (
    <div style={{ width: '100%', maxWidth: '72rem', marginLeft: 'auto', marginRight: 'auto', padding: '0.5rem', backgroundColor: 'transparent', color: 'white' }}>

      <div style={{ backgroundColor: 'transparent', borderRadius: '0.5rem', padding: '1.5rem' }}>
        {/* Chart Section */}
        <div style={{ marginBottom: '2rem' }}>
          <div style={{ marginBottom: '1rem' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.5rem' }}>{t('charts.spider.totalGlobalProduction')}</h3>
            
            {/* Material indicators */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', marginBottom: '1rem' }}>
              {getCurrentMaterials().map((material, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div 
                    style={{
                      width: '0.75rem',
                      height: '0.75rem',
                      borderRadius: '9999px',
                      backgroundColor: material.color
                    }}
                  ></div>
                  <span style={{ fontSize: '0.875rem' }}>
                    {material.name} - {formatNumber(material.totalProduction)} mt
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Radar Chart */}
          <div style={{ height: '20rem', marginBottom: '1.5rem' }}>
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={getRadarData}>
                <PolarGrid stroke="#374151" />
                <PolarAngleAxis 
                  dataKey="country" 
                  tickFormatter={(value) => (String(value || "").startsWith("—") ? "" : value)}
                  tick={{ fontSize: 12, fill: '#9CA3AF' }}
                />
                {/* <PolarRadiusAxis
                  domain={getRadarDomain}
                  tickFormatter={() => ''}
                  axisLine={false}
                  tick={false}
                /> */}
                
                {/* Material 1 - Always shown */}
                <Radar
                  name="Material 1"
                  dataKey="Material1"
                  stroke={materialsData.material1.color}
                  fill={materialsData.material1.color}
                  fillOpacity={viewMode === 'single' ? 0.3 : 0.15}
                  strokeWidth={2}
                />
                
                {/* Material 2 - Shown in dual and triple view */}
                {(viewMode === 'dual' || viewMode === 'triple') && (
                  <Radar
                    name="Material 2"
                    dataKey="Material2"
                    stroke={materialsData.material2.color}
                    fill={materialsData.material2.color}
                    fillOpacity={0.15}
                    strokeWidth={2}
                  />
                )}
                
                {/* Material 3 - Shown only in triple view */}
                {viewMode === 'triple' && (
                  <Radar
                    name="Material 3"
                    dataKey="Material3"
                    stroke={materialsData.material3.color}
                    fill={materialsData.material3.color}
                    fillOpacity={0.15}
                    strokeWidth={2}
                  />
                )}
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Tabs for multiple materials */}
        {(viewMode === 'dual' || viewMode === 'triple') && (
          <div className="chart-type-selector" style={{ marginBottom: '1rem' }}>
            {getCurrentMaterials().map((material, index) => {
              const materialKey = `material${index + 1}`;
              return (
                <button
                  key={materialKey}
                  onClick={() => setActiveTab(materialKey)}
                  className={`chart-btn chart-btn-icon-only ${activeTab === materialKey ? 'active' : ''}`}
                >
                  {shortenName(material.name)}
                </button>
              );
            })}
          </div>
        )}

        {/* Data Table */}
        <div style={{ backgroundColor: 'transparent', borderRadius: '0.5rem', padding: '0rem' }}>
          <div>
            {getDisplayedMaterial().countries.map((country, index) => (
              <div key={index} style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                paddingTop: '0.5rem', 
                paddingBottom: '0.5rem', 
                borderBottom: index === getDisplayedMaterial().countries.length - 1 ? 'none' : '1px solid #4B5563'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div 
                    style={{
                      width: '0.5rem',
                      height: '0.5rem',
                      borderRadius: '9999px',
                      backgroundColor: getDisplayedMaterial().color
                    }}
                  ></div>
                  <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>
                    {country.country} - {country.name}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', fontSize: '0.875rem' }}>
                  <span style={{ color: '#D1D5DB' }}>{formatNumber(country.value)} mt</span>
                  <span style={{ color: '#9CA3AF' }}>{country.change}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpiderChart;
